<script>
import axios from 'axios';
import { api, aviso, erro, sucesso } from '@/helpers/common/api';
import ContatoModal from '@/components/crm/ContatoModal.vue';
import GridContatoColunasSelector from '@/components/crm/contato/GridContatoColunasSelector.vue';
import AdicionarAGrupoModal from '@/components/crm/contato/AdicionarAGrupoModal.vue';

const STORAGE_KEY = 'crm-contato-grid-colunas';
const STORAGE_KEY_PER_PAGE = 'crm-contato-registros-por-pagina';

export default {
  name: 'ContatoGrid',
  components: { ContatoModal, GridContatoColunasSelector, AdicionarAGrupoModal },
  data() {
    return {
      contatos: [],
      loading: false,
      // Busca separada por campo: nome (nome/sobrenome/nome completo),
      // telefone (canais) e cidade.
      filtros: { nome: '', telefone: '', cidade: '', status: '', rotulosIds: [], estados: [] },
      rotulosDisponiveis: [],
      listaUFs: [
        'AC','AL','AM','AP','BA','CE','DF','ES','GO','MA','MG','MS','MT',
        'PA','PB','PE','PI','PR','RJ','RN','RO','RR','RS','SC','SE','SP','TO',
      ],
      exportando: '',
      paginacao: {
        paginaAtual: 1,
        registrosPorPagina: this.carregarRegistrosPorPagina(),
        totalRegistros: 0,
      },
      modalAberto: false,
      seletorColunasAberto: false,
      contatoEditando: null,
      // Modal "adicionar contato a um grupo de WhatsApp".
      grupoModalAberto: false,
      contatoParaGrupo: null,
      debounceTimer: null,
      seqCarga: 0,
      camposDisponiveis: [],
      colunasSelecionadas: this.carregarColunasLocal(),
      colunasFixas: [
        { id: 'id', label: 'ID', padrao: false, ordenavel: true, campoDb: 'id' },
        { id: 'nome', label: 'Nome', obrigatoria: true, padrao: true, ordenavel: true, campoDb: 'nome' },
        { id: 'telefone', label: 'Telefone', padrao: true },
        { id: 'cidade', label: 'Cidade', padrao: true, ordenavel: true, campoDb: 'cidade' },
        { id: 'estado', label: 'Estado', padrao: true, ordenavel: true, campoDb: 'estado' },
        { id: 'email', label: 'E-mail', padrao: true },
        { id: 'status', label: 'Status', padrao: true, ordenavel: true, campoDb: 'status_contato' },
        { id: 'rotulos', label: 'Etiquetas', padrao: true },
      ],
      ordenacao: { campo: 'nome', direcao: 'asc' },
    };
  },
  computed: {
    colunasFixasVisiveis() {
      return this.colunasFixas.filter((coluna) => coluna.obrigatoria || this.colunasSelecionadas.fixas.includes(coluna.id));
    },
    colunasCustomVisiveis() {
      return this.camposDisponiveis.filter((campo) => this.colunasSelecionadas.personalizadas.includes(campo.id));
    },
    totalColunasGrid() {
      return this.colunasFixasVisiveis.length + this.colunasCustomVisiveis.length + 1;
    },
    totalPaginas() {
      return Math.ceil(this.paginacao.totalRegistros / this.paginacao.registrosPorPagina) || 1;
    },
    temPaginaAnterior() {
      return this.paginacao.paginaAtual > 1;
    },
    temProximaPagina() {
      return this.paginacao.paginaAtual < this.totalPaginas;
    },
  },
  async created() {
    // A listagem não depende de campos/colunas/etiquetas: dispara tudo junto
    // em vez de esperar duas idas ao servidor antes de buscar os contatos.
    await Promise.all([
      this.carregarCamposDisponiveis(),
      this.carregarPreferenciasColunas(),
      this.carregarContatos(),
      this.carregarRotulos(),
    ]);
  },
  methods: {
    carregarRegistrosPorPagina() {
      try {
        const val = Number(window.localStorage.getItem(STORAGE_KEY_PER_PAGE));
        if ([25, 50, 100, 250].includes(val)) return val;
      } catch (_error) {
        return 25;
      }
      return 25;
    },
    carregarColunasLocal() {
      try {
        const data = JSON.parse(window.localStorage.getItem(STORAGE_KEY) || 'null');
        if (data && Array.isArray(data.fixas) && Array.isArray(data.personalizadas)) return data;
      } catch (_error) {
        return { fixas: ['nome', 'telefone', 'cidade', 'estado', 'email', 'status', 'rotulos'], personalizadas: [] };
      }
      return { fixas: ['nome', 'telefone', 'cidade', 'estado', 'email', 'status', 'rotulos'], personalizadas: [] };
    },
    salvarColunasLocal() {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(this.colunasSelecionadas));
    },
    async carregarCamposDisponiveis() {
      this.camposDisponiveis = await api.read('/campopersonalizado?ativo=true') || [];
    },
    async carregarRotulos() {
      const resultado = await api.read('/rotulo?limit=9999');
      this.rotulosDisponiveis = (Array.isArray(resultado) ? resultado : resultado?.data || []).map((r) => ({
        value: r.id,
        label: r.nome,
        cor: r.cor_referencia || '#6c757d',
      }));
    },
    async carregarPreferenciasColunas() {
      const preferencias = await api.read('/preferenciagridcontato');
      if (preferencias) {
        this.colunasSelecionadas = {
          fixas: Array.isArray(preferencias.colunas_fixas) && preferencias.colunas_fixas.length
            ? preferencias.colunas_fixas
            : this.carregarColunasLocal().fixas,
          personalizadas: Array.isArray(preferencias.colunas_personalizadas)
            ? preferencias.colunas_personalizadas.map((id) => Number(id))
            : this.carregarColunasLocal().personalizadas,
        };
      }
      this.salvarColunasLocal();
    },
    async salvarPreferenciasColunas(payload) {
      const response = await api.create('/preferenciagridcontato', {
        colunasFixas: payload.fixas,
        colunasPersonalizadas: payload.personalizadas,
      });

      if (!response) {
        erro({ title: 'Erro ao salvar as colunas da grade.' });
        return;
      }

      this.colunasSelecionadas = {
        fixas: response.colunas_fixas,
        personalizadas: response.colunas_personalizadas,
      };
      this.salvarColunasLocal();
      this.seletorColunasAberto = false;
      sucesso({ title: 'Preferências da grade atualizadas!' });
    },
    /** Filtros compartilhados entre a listagem e a exportação. */
    montarParamsFiltros() {
      const params = new URLSearchParams();
      if (this.filtros.nome) params.set('filtroNome', this.filtros.nome);
      if (this.filtros.telefone) params.set('filtroTelefone', this.filtros.telefone);
      if (this.filtros.cidade) params.set('filtroCidade', this.filtros.cidade);
      if (this.filtros.status) params.set('status', this.filtros.status);
      if (this.filtros.rotulosIds && this.filtros.rotulosIds.length) params.set('rotulosIds', this.filtros.rotulosIds.join(','));
      if (this.filtros.estados && this.filtros.estados.length) params.set('filtroEstados', this.filtros.estados.join(','));
      if (this.ordenacao.campo) {
        params.set('order', JSON.stringify([[this.ordenacao.campo, this.ordenacao.direcao]]));
      }
      return params;
    },
    async carregarContatos() {
      // Só a resposta da requisição MAIS RECENTE vale. Sem isso, a carga
      // anterior (ex.: a inicial, sem filtro e mais lenta) chegava depois da
      // filtrada e sobrescrevia a grade — contatos sem a etiqueta escolhida.
      this.seqCarga += 1;
      const seq = this.seqCarga;
      this.loading = true;
      try {
        const params = this.montarParamsFiltros();
        const offset = (this.paginacao.paginaAtual - 1) * this.paginacao.registrosPorPagina;
        params.set('offset', String(offset));
        params.set('limit', String(this.paginacao.registrosPorPagina));
        const qs = params.toString();
        const resultado = await api.read(`/contato${qs ? `?${qs}` : ''}`);
        if (seq !== this.seqCarga) return;
        if (resultado && typeof resultado === 'object') {
          this.contatos = Array.isArray(resultado) ? resultado : resultado.data || [];
          if (resultado.total !== undefined) {
            this.paginacao.totalRegistros = resultado.total;
          }
        }
      } finally {
        if (seq === this.seqCarga) this.loading = false;
      }
    },
    /**
     * Baixa os contatos filtrados no formato pedido. Exporta exatamente os
     * mesmos filtros e as mesmas colunas visíveis na grade — sem paginação.
     */
    async exportar(formato) {
      if (this.exportando) return;
      this.exportando = formato;
      try {
        const params = this.montarParamsFiltros();
        params.set('formato', formato);
        params.set('colunas', this.colunasFixasVisiveis.map((c) => c.id).join(','));
        params.set('camposIds', this.colunasCustomVisiveis.map((c) => c.id).join(','));
        // Só para imprimir no cabeçalho do PDF (o filtro em si vai por id).
        const idsEtiquetas = this.filtros.rotulosIds || [];
        const etiquetas = this.rotulosDisponiveis
          .filter((r) => idsEtiquetas.includes(r.value))
          .map((r) => r.label);
        if (etiquetas.length) params.set('rotulosLabels', etiquetas.join(', '));

        const resposta = await axios.get(`/contato/exportar?${params.toString()}`, { responseType: 'blob' });

        const total = Number(resposta.headers['x-total-registros'] || 0);
        const exportados = Number(resposta.headers['x-registros-exportados'] || 0);

        const nomeArquivo = this.nomeArquivoDeResposta(resposta) || `contatos.${formato}`;
        const url = window.URL.createObjectURL(resposta.data);
        const link = document.createElement('a');
        link.href = url;
        link.download = nomeArquivo;
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);

        if (total && exportados && exportados < total) {
          aviso({
            title: 'Exportação parcial',
            text: `Foram exportados ${exportados} de ${total} contatos (limite do formato ${formato.toUpperCase()}). Refine os filtros para exportar o restante.`,
          });
        } else {
          sucesso({ title: `Exportação ${formato.toUpperCase()} concluída!` });
        }
      } catch (_error) {
        erro({ title: 'Não foi possível exportar os contatos.' });
      } finally {
        this.exportando = '';
      }
    },
    nomeArquivoDeResposta(resposta) {
      const disposicao = resposta?.headers?.['content-disposition'] || '';
      const match = disposicao.match(/filename="?([^";]+)"?/i);
      return match ? match[1] : '';
    },
    debounceFiltros() {
      clearTimeout(this.debounceTimer);
      this.paginacao.paginaAtual = 1;
      this.debounceTimer = setTimeout(() => {
        this.carregarContatos();
      }, 800);
    },
    ordenarPor(coluna) {
      if (!coluna.ordenavel) return;
      const campo = coluna.campoDb || coluna.id;
      if (this.ordenacao.campo === campo) {
        this.ordenacao.direcao = this.ordenacao.direcao === 'asc' ? 'desc' : 'asc';
      } else {
        this.ordenacao.campo = campo;
        this.ordenacao.direcao = 'asc';
      }
      this.paginacao.paginaAtual = 1;
      this.carregarContatos();
    },
    iconeOrdenacao(coluna) {
      if (!coluna.ordenavel) return '';
      const campo = coluna.campoDb || coluna.id;
      if (this.ordenacao.campo !== campo) return 'ri-arrow-up-down-line text-muted opacity-50';
      return this.ordenacao.direcao === 'asc' ? 'ri-arrow-up-s-fill text-primary' : 'ri-arrow-down-s-fill text-primary';
    },
    irParaPagina(pagina) {
      if (pagina >= 1 && pagina <= this.totalPaginas) {
        this.paginacao.paginaAtual = pagina;
        this.carregarContatos();
      }
    },
    mudarRegistrosPorPagina(novoLimite) {
      this.paginacao.registrosPorPagina = Number(novoLimite);
      this.paginacao.paginaAtual = 1;
      window.localStorage.setItem(STORAGE_KEY_PER_PAGE, String(novoLimite));
      this.carregarContatos();
    },
    formatarTelefone(valor) {
      const digits = String(valor || '').replace(/\D/g, '');
      if (!digits) return '—';

      const formatarBr = (brDigits) => {
        if (brDigits.length < 10) return brDigits;
        const ddd = brDigits.slice(0, 2);
        const numero = brDigits.slice(2);

        if (numero.length >= 9) {
          return `(${ddd}) ${numero.slice(0, 5)}-${numero.slice(5, 9)}`;
        }

        return `(${ddd}) ${numero.slice(0, 4)}-${numero.slice(4, 8)}`;
      };

      if (digits.startsWith('55')) {
        return formatarBr(digits.slice(2));
      }

      if (digits.length === 10 || digits.length === 11) {
        return formatarBr(digits);
      }

      if (digits.length > 5) {
        return `(${digits.slice(0, 5)}) ${digits.slice(5)}`;
      }

      return digits;
    },
    telefoneCanalPrincipal(contato) {
      // Prioriza o marcado como principal, mas cai no primeiro telefone
      // disponível — contatos importados nem sempre têm a flag preenchida.
      const telefones = (contato.canais || []).filter((c) => c.tipo_canal === 'telefone');
      return telefones.find((c) => c.principal) || telefones[0] || null;
    },
    telefonePrincipal(contato) {
      const canal = this.telefoneCanalPrincipal(contato);
      return canal ? this.formatarTelefone(canal.valor_canal) : '—';
    },
    /** Telefone principal em dígitos (DDI+DDD) — usado nas ações de WhatsApp. */
    telefonePrincipalRaw(contato) {
      const canal = this.telefoneCanalPrincipal(contato);
      if (!canal) return '';
      return String(canal.normalizado || canal.valor_canal || '').replace(/\D/g, '');
    },
    temTelefone(contato) {
      return Boolean(this.telefonePrincipalRaw(contato));
    },
    emailPrincipal(contato) {
      const canal = (contato.canais || []).find((c) => c.tipo_canal === 'email');
      return canal ? canal.valor_canal : '—';
    },
    statusBadge(status) {
      const map = {
        ativo: 'badge bg-success-subtle text-success',
        inativo: 'badge bg-secondary-subtle text-secondary',
        bloqueado: 'badge bg-danger-subtle text-danger',
      };
      return map[status] || 'badge bg-secondary-subtle text-secondary';
    },
    capitalizar(texto) {
      if (!texto) return '—';
      return texto.charAt(0).toUpperCase() + texto.slice(1);
    },
    valorCampoPersonalizado(contato, campo) {
      const registro = (contato.camposPersonalizados || []).find((item) => item.campopersonalizado_id === campo.id);
      if (!registro) return '—';
      if (Array.isArray(registro.valor)) return registro.valor.join(', ') || '—';
      if (typeof registro.valor === 'boolean') return registro.valor ? 'Sim' : 'Não';
      return registro.valor || '—';
    },
    abrirModal(contato = null) {
      this.contatoEditando = contato;
      this.modalAberto = true;
    },
    fecharModal() {
      this.modalAberto = false;
      this.contatoEditando = null;
    },
    /**
     * Abre a conversa de WhatsApp com o contato no módulo de Atendimento
     * (canal oficial), reusando o fluxo/validações de "iniciar conversa".
     */
    enviarWhatsapp(contato) {
      if (!this.temTelefone(contato)) return;
      this.$router.push({
        path: '/app/atendimento',
        query: {
          iniciarContatoId: contato.id,
          iniciarContatoNome: contato.nome_completo || contato.nome || '',
        },
      });
    },
    abrirAdicionarGrupo(contato) {
      if (!this.temTelefone(contato)) return;
      this.contatoParaGrupo = {
        id: contato.id,
        nome: contato.nome_completo || contato.nome || `Contato #${contato.id}`,
        telefone: this.telefonePrincipalRaw(contato),
      };
      this.grupoModalAberto = true;
    },
    fecharAdicionarGrupo() {
      this.grupoModalAberto = false;
      this.contatoParaGrupo = null;
    },
    onContatoSalvo() {
      this.fecharModal();
      this.paginacao.paginaAtual = 1;
      this.carregarContatos();
    },
  },
};
</script>

<template>
  <div class="card-body">
    <!-- Toolbar de filtros -->
    <div class="crm-toolbar-filtros">
      <div class="crm-filtro-nome">
        <div class="search-box">
          <input
            v-model="filtros.nome"
            type="text"
            class="form-control search"
            placeholder="Nome..."
            @input="debounceFiltros"
          />
          <i class="ri-user-3-line search-icon"></i>
        </div>
      </div>
      <div class="crm-filtro-telefone">
        <div class="search-box">
          <input
            v-model="filtros.telefone"
            type="text"
            class="form-control search"
            placeholder="Telefone..."
            @input="debounceFiltros"
          />
          <i class="ri-phone-line search-icon"></i>
        </div>
      </div>
      <div class="crm-filtro-cidade">
        <div class="search-box">
          <input
            v-model="filtros.cidade"
            type="text"
            class="form-control search"
            placeholder="Cidade..."
            @input="debounceFiltros"
          />
          <i class="ri-map-pin-line search-icon"></i>
        </div>
      </div>
      <div class="crm-filtro-uf">
        <Multiselect
          v-model="filtros.estados"
          :options="listaUFs"
          mode="tags"
          :searchable="true"
          :close-on-select="false"
          placeholder="UF"
          no-results-text="Não encontrada"
          @change="debounceFiltros"
        />
      </div>
      <div class="crm-filtro-status">
        <Multiselect
          v-model="filtros.status"
          :options="[
            { value: '', label: 'Todos os status' },
            { value: 'ativo', label: 'Ativo' },
            { value: 'inativo', label: 'Inativo' },
            { value: 'bloqueado', label: 'Bloqueado' },
          ]"
          label="label"
          value-prop="value"
          :searchable="false"
          :can-clear="false"
          :can-deselect="false"
          placeholder="Status"
          @change="debounceFiltros"
        />
      </div>
      <div class="crm-filtro-rotulos">
        <Multiselect
          v-model="filtros.rotulosIds"
          :options="rotulosDisponiveis"
          mode="tags"
          label="label"
          value-prop="value"
          :searchable="true"
          :close-on-select="false"
          placeholder="Etiquetas"
          no-results-text="Nenhuma etiqueta"
          @change="debounceFiltros"
        >
          <template #tag="{ option, handleTagRemove }">
            <span class="multiselect-tag" :style="{ background: option.cor }">
              {{ option.label }}
              <span class="multiselect-tag-remove" @click.prevent="handleTagRemove(option, $event)">
                <span class="multiselect-tag-remove-icon"></span>
              </span>
            </span>
          </template>
          <template #option="{ option }">
            <span class="badge me-2" :style="{ backgroundColor: option.cor }">&nbsp;</span>
            {{ option.label }}
          </template>
        </Multiselect>
      </div>
      <div class="crm-filtro-acoes">
        <BDropdown variant="soft-dark" :text="`${paginacao.registrosPorPagina} / pág`" class="me-1">
          <BDropdownItem @click="mudarRegistrosPorPagina(25)">25</BDropdownItem>
          <BDropdownItem @click="mudarRegistrosPorPagina(50)">50</BDropdownItem>
          <BDropdownItem @click="mudarRegistrosPorPagina(100)">100</BDropdownItem>
          <BDropdownItem @click="mudarRegistrosPorPagina(250)">250</BDropdownItem>
        </BDropdown>
        <BDropdown variant="soft-success" class="me-1" :disabled="!!exportando" no-caret>
          <template #button-content>
            <BSpinner v-if="exportando" small />
            <i v-else class="ri-download-2-line"></i>
          </template>
          <BDropdownHeader>Exportar contatos filtrados</BDropdownHeader>
          <BDropdownItem @click="exportar('csv')">
            <i class="ri-file-excel-2-line align-bottom me-2 text-success"></i> CSV
          </BDropdownItem>
          <BDropdownItem @click="exportar('pdf')">
            <i class="ri-file-pdf-2-line align-bottom me-2 text-danger"></i> PDF
          </BDropdownItem>
        </BDropdown>
        <BButton variant="soft-secondary" class="me-1" @click="seletorColunasAberto = true">
          <i class="ri-settings-3-line"></i>
        </BButton>
        <nav aria-label="Paginação topo">
          <BPagination
            v-model="paginacao.paginaAtual"
            :total-rows="paginacao.totalRegistros"
            :per-page="paginacao.registrosPorPagina"
            :limit="3"
            align="end"
            class="mb-0"
            @update:modelValue="irParaPagina"
          />
        </nav>
      </div>
    </div>

    <div v-if="loading" class="text-center py-4">
      <BSpinner variant="primary" />
    </div>
    <div v-else class="table-responsive">
      <table class="table table-striped table-hover align-middle table-nowrap mb-0">
        <thead class="table-light">
          <tr>
            <th
              v-for="coluna in colunasFixasVisiveis"
              :key="coluna.id"
              :class="{ 'crm-th-ordenavel': coluna.ordenavel }"
              @click="ordenarPor(coluna)"
            >
              {{ coluna.label }}
              <i v-if="coluna.ordenavel" :class="iconeOrdenacao(coluna)" class="ms-1 fs-13"></i>
            </th>
            <th v-for="campo in colunasCustomVisiveis" :key="campo.id">{{ campo.nome_exibicao }}</th>
            <th class="text-end">Ações</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="contato in contatos" :key="contato.id">
            <td v-if="colunasSelecionadas.fixas.includes('id')" class="text-muted">{{ contato.id }}</td>
            <td v-if="colunasSelecionadas.fixas.includes('nome')">{{ contato.nome_completo || contato.nome || '—' }}</td>
            <td v-if="colunasSelecionadas.fixas.includes('telefone')">{{ telefonePrincipal(contato) }}</td>
            <td v-if="colunasSelecionadas.fixas.includes('cidade')">{{ contato.cidade || '—' }}</td>
            <td v-if="colunasSelecionadas.fixas.includes('estado')">{{ contato.estado || '—' }}</td>
            <td v-if="colunasSelecionadas.fixas.includes('email')">{{ emailPrincipal(contato) }}</td>
            <td v-if="colunasSelecionadas.fixas.includes('status')">
              <span :class="statusBadge(contato.status_contato)">{{ capitalizar(contato.status_contato) }}</span>
            </td>
            <td v-if="colunasSelecionadas.fixas.includes('rotulos')">
              <span
                v-for="rotulo in contato.rotulos"
                :key="rotulo.id"
                class="badge me-1"
                :style="{ backgroundColor: rotulo.cor_referencia || '#6c757d' }"
              >{{ rotulo.nome }}</span>
              <span v-if="!contato.rotulos?.length">—</span>
            </td>
            <td v-for="campo in colunasCustomVisiveis" :key="`${contato.id}-${campo.id}`">{{ valorCampoPersonalizado(contato, campo) }}</td>
            <td class="text-end">
              <div class="btn-group btn-group-sm" role="group">
                <BButton
                  variant="soft-success"
                  size="sm"
                  :disabled="!temTelefone(contato)"
                  :title="temTelefone(contato) ? 'Enviar WhatsApp / abrir conversa' : 'Sem telefone cadastrado'"
                  @click="enviarWhatsapp(contato)"
                >
                  <i class="ri-whatsapp-line"></i>
                </BButton>
                <BButton
                  variant="soft-primary"
                  size="sm"
                  :disabled="!temTelefone(contato)"
                  :title="temTelefone(contato) ? 'Adicionar a um grupo de WhatsApp' : 'Sem telefone cadastrado'"
                  @click="abrirAdicionarGrupo(contato)"
                >
                  <i class="ri-group-line"></i>
                </BButton>
                <BButton variant="soft-info" size="sm" title="Editar contato" @click="abrirModal(contato)">
                  <i class="ri-edit-line"></i>
                </BButton>
              </div>
            </td>
          </tr>
          <tr v-if="!contatos.length">
            <td :colspan="totalColunasGrid" class="text-center text-muted py-4">Nenhum contato encontrado.</td>
          </tr>
        </tbody>
      </table>
    </div>

    <BRow class="mt-3 align-items-center">
      <BCol md="6" class="d-flex align-items-center">
        <small class="text-muted">
          Mostrando <strong>{{ (paginacao.paginaAtual - 1) * paginacao.registrosPorPagina + 1 }}</strong>
          a
          <strong>{{ Math.min(paginacao.paginaAtual * paginacao.registrosPorPagina, paginacao.totalRegistros) }}</strong>
          de
          <strong>{{ paginacao.totalRegistros }}</strong>
          registros
        </small>
      </BCol>
      <BCol md="6" class="text-end">
        <nav aria-label="Paginação">
          <BPagination
            v-model="paginacao.paginaAtual"
            :total-rows="paginacao.totalRegistros"
            :per-page="paginacao.registrosPorPagina"
            :limit="5"
            align="end"
            @update:modelValue="irParaPagina"
          />
        </nav>
      </BCol>
    </BRow>
  </div>

  <ContatoModal
    :visivel="modalAberto"
    :contato="contatoEditando"
    :campos-disponiveis="camposDisponiveis"
    @fechar="fecharModal"
    @salvo="onContatoSalvo"
  />

  <GridContatoColunasSelector
    :visivel="seletorColunasAberto"
    :colunas-fixas="colunasFixas"
    :campos-personalizados="camposDisponiveis"
    :selecionadas="colunasSelecionadas"
    @fechar="seletorColunasAberto = false"
    @salvar="salvarPreferenciasColunas"
  />

  <AdicionarAGrupoModal
    :visivel="grupoModalAberto"
    :contato="contatoParaGrupo"
    @fechar="fecharAdicionarGrupo"
  />

  <!-- FAB Novo Contato -->
  <button
    type="button"
    class="crm-fab-novo"
    title="Novo Contato"
    @click="abrirModal()"
  >
    <i class="ri-add-line"></i>
    <span class="crm-fab-label">Novo Contato</span>
  </button>
</template>

<style scoped>
/* Toolbar de filtros */
.crm-toolbar-filtros {
  display: flex;
  gap: 0.5rem;
  align-items: center;
  margin-bottom: 1rem;
  flex-wrap: wrap;
}
.crm-filtro-nome {
  flex: 1 1 200px;
  min-width: 160px;
}
.crm-filtro-telefone {
  flex: 0 1 170px;
  min-width: 140px;
}
.crm-filtro-cidade {
  flex: 0 1 180px;
  min-width: 140px;
}
.crm-filtro-uf {
  flex: 0 0 130px;
}
.crm-filtro-status {
  flex: 0 0 150px;
}
.crm-filtro-rotulos {
  flex: 1 1 220px;
  min-width: 180px;
}
.crm-filtro-rotulos :deep(.multiselect-tags) {
  flex-wrap: nowrap;
  overflow-x: auto;
  overflow-y: hidden;
  scrollbar-width: none;
  padding-right: 2rem;
}
.crm-filtro-rotulos :deep(.multiselect-tags::-webkit-scrollbar) {
  display: none;
}
.crm-filtro-rotulos :deep(.multiselect-tag) {
  flex-shrink: 0;
}
.crm-filtro-acoes {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  gap: 0.25rem;
  margin-left: auto;
}
.crm-filtro-acoes :deep(.btn),
.crm-filtro-acoes :deep(.dropdown-toggle) {
  min-height: 38px;
  display: inline-flex;
  align-items: center;
}
.crm-filtro-acoes :deep(.pagination) {
  min-height: 38px;
  align-items: center;
}

@media (max-width: 767.98px) {
  .crm-filtro-nome {
    flex: 1 1 100%;
  }
  .crm-filtro-telefone,
  .crm-filtro-cidade {
    flex: 1 1 calc(50% - 0.25rem);
  }
  .crm-filtro-uf {
    flex: 1 1 calc(50% - 0.25rem);
  }
  .crm-filtro-status {
    flex: 1 1 calc(50% - 0.25rem);
  }
  .crm-filtro-rotulos {
    flex: 1 1 100%;
  }
  .crm-filtro-acoes {
    margin-left: 0;
    flex: 1 1 100%;
    justify-content: space-between;
  }
}

/* Headers ordenáveis */
.crm-th-ordenavel {
  cursor: pointer;
  user-select: none;
  white-space: nowrap;
  transition: color 0.15s;
}
.crm-th-ordenavel:hover {
  color: var(--vz-primary, #405189);
}

/* FAB flutuante */
.crm-fab-novo {
  position: fixed;
  bottom: 2rem;
  right: 2rem;
  z-index: 1040;
  height: 52px;
  border-radius: 26px;
  border: none;
  background-color: var(--vz-success, #0ab39c);
  color: #fff;
  font-size: 20px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0 1.25rem;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.25);
  cursor: pointer;
  transition: transform 0.2s, box-shadow 0.2s;
}
.crm-fab-novo:hover {
  transform: scale(1.05);
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.3);
}
.crm-fab-novo:active {
  transform: scale(0.97);
}
.crm-fab-label {
  font-size: 14px;
  font-weight: 600;
  white-space: nowrap;
}

/* Mobile: esconder label, voltar a formato circular */
@media (max-width: 767.98px) {
  .crm-fab-label {
    display: none;
  }
  .crm-fab-novo {
    width: 52px;
    padding: 0;
    border-radius: 50%;
  }
}
</style>
